import { useState, useEffect, useMemo } from 'react'
import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAvatars } from '../../lib/useAvatars'
import PersonAvatarFace from '../PersonAvatarFace'
import { stripTeamBracket } from '../Pick/ui'
import RiderRow from './RiderRow'
import { loadRiderInfo } from '../Pick/riderInfo'
import { fmtGap } from '../Pick/odrResults'

// Scheda del giocatore aperta dalla classifica GENERALE di un giro: in una classifica cumulata ha senso vedere COME ci e' arrivato,
// quindi una sezione per ogni tappa fino a quella selezionata (la piu' recente in alto): corridore/i scelti con il loro piazzamento
// e, a destra, il valore di quella tappa nella classifica che stai guardando:
//   tempo  -> posizione e distacco del giocatore in lega in quella tappa
//   punti  -> punti maglia verde presi in quella tappa
//   gpm    -> punti GPM (collinari compresi) presi in quella tappa
// player: { id, name, pos?, value?, type } (pos e value sono quelli della classifica generale, come nella scheda di tappa)
export default function PickModalGenerale({ player, idStage, leagueId, raceId, onClose }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const insets = useSafeAreaInsets()
  const { user } = useAuth()
  const avatars = useAvatars([player.id])
  const [data, setData] = useState(null)      // null = in caricamento
  const isMe = user?.id === player.id
  const type = player.type ?? 'tempo'

  useEffect(() => {
    if (!player?.id || !idStage || !leagueId || !raceId) return
    let cancelled = false
    ;(async () => {
      const stRes = await supabase.from('dim_race_stage').select('id_stage, id_stage_number, stage_date').eq('id_race', raceId).order('id_stage_number')
      const all = stRes.data ?? []
      const cur = all.find(s => s.id_stage === idStage)
      const stages = all.filter(s => !cur || s.id_stage_number <= cur.id_stage_number)
      const stageIds = stages.map(s => s.id_stage)

      const [pRes, fRes, tRes, puRes] = await Promise.all([
        supabase.from('fact_user_pick').select('id_stage, id_slot, id_rider').eq('id_user', player.id).eq('id_league', leagueId).in('id_stage', stageIds),
        supabase.from('fact_user_flip').select('id_rider, id_slot, id_stage_used').eq('id_user', player.id).eq('id_race', raceId).eq('id_league', leagueId),
        supabase.rpc('fn_classifica_tappa', { p_id_league: leagueId }).select('id_stage, posizione, gap_stage').eq('id_user', player.id).eq('id_race', raceId),
        supabase.rpc('fn_classifica_tappa_punti', { p_id_league: leagueId }).select('id_stage, punti_verde, punti_hilly, punti_gpm').eq('id_user', player.id).eq('id_race', raceId),
      ])
      if (cancelled) return

      const picksByStage = {}
      ;(pRes.data ?? []).forEach(r => { (picksByStage[r.id_stage] ??= []).push({ slot: r.id_slot, id: r.id_rider }) })
      Object.values(picksByStage).forEach(l => l.sort((a, b) => a.slot - b.slot))
      const flipByStage = {}
      ;(fRes.data ?? []).forEach(f => { flipByStage[f.id_stage_used] = f })
      const tappaByStage = {}
      ;(tRes.data ?? []).forEach(r => { tappaByStage[r.id_stage] = r })
      const puntiByStage = {}
      ;(puRes.data ?? []).forEach(r => { puntiByStage[r.id_stage] = r })

      const riderIds = [...new Set([
        ...(pRes.data ?? []).map(r => r.id_rider),
        ...Object.values(flipByStage).map(f => f.id_rider),
      ])].filter(Boolean)
      const [riders, resRes] = await Promise.all([
        loadRiderInfo(riderIds),
        riderIds.length
          ? supabase.from('fact_results').select('id_stage, id_rider, rank_stage, gap_stage, is_dnf').in('id_stage', stageIds).in('id_rider', riderIds).eq('is_published', true)
          : Promise.resolve({ data: [] }),
      ])
      if (cancelled) return
      const results = {}
      ;(resRes.data ?? []).forEach(r => { results[`${r.id_stage}:${r.id_rider}`] = { rank: r.rank_stage, gap: r.gap_stage, dnf: r.is_dnf } })

      // piu' recente in alto; la tappa senza risultati pubblicati non si mostra (non e' ancora nella classifica)
      const rows = [...stages].reverse().filter(st => tappaByStage[st.id_stage] || puntiByStage[st.id_stage] || (picksByStage[st.id_stage] ?? []).some(p => results[`${st.id_stage}:${p.id}`]))
        .map(st => ({
          stage: st,
          picks: (picksByStage[st.id_stage] ?? []).map(p => ({ ...p, res: results[`${st.id_stage}:${p.id}`] ?? null })),
          flip: flipByStage[st.id_stage] ?? null,
          tappa: tappaByStage[st.id_stage] ?? null,
          punti: puntiByStage[st.id_stage] ?? null,
        }))
      setData({ rows, riders })
    })().catch(() => { if (!cancelled) setData({ rows: [], riders: {}, error: true }) })
    return () => { cancelled = true }
  }, [player?.id, idStage, leagueId, raceId])

  const nameOf = (rd, fallback) => rd ? (rd.labelNew || rd.label) : fallback

  // valore della tappa nella classifica guardata
  const stageValue = (row) => {
    if (type === 'tempo') {
      if (!row.tappa) return null
      return { main: `${row.tappa.posizione}°`, sub: fmtGap(row.tappa.gap_stage) || null }
    }
    if (!row.punti) return null
    const pts = type === 'punti' ? Number(row.punti.punti_verde ?? 0) : Number(row.punti.punti_hilly ?? 0) + Number(row.punti.punti_gpm ?? 0)
    return { main: pts > 0 ? `+${pts} pt` : '0 pt', sub: null, positive: pts > 0 }
  }

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
        <View style={[styles.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
          <View style={[styles.header, { borderBottomColor: tc.border }]}>
            <PersonAvatarFace userId={player.id} settings={avatars[player.id]} size={44} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={[styles.title, { color: isMe ? tc.rosaText : tc.text }]} numberOfLines={1}>{player.name}</Text>
              {(player.pos || player.value) ? (
                <Text style={[styles.sub, { color: tc.textMuted }]} numberOfLines={1}>
                  {[player.pos ? `${player.pos}°` : null, player.value].filter(Boolean).join(' · ')}
                </Text>
              ) : null}
            </View>
            <Pressable onPress={onClose} hitSlop={6} style={[styles.close, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={18} color={tc.text} />
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={{ padding: 12, gap: 14 }}>
            {data === null ? (
              <Text style={[styles.empty, { color: tc.textMuted }]}>{t('common.loading')}</Text>
            ) : data.rows.length === 0 ? (
              <Text style={[styles.empty, { color: tc.textMuted }]}>{t('pick.no_pick_stage')}</Text>
            ) : (
              <>
                <Text style={[styles.kicker, { color: tc.textMuted }]} accessibilityRole="header">{t('leaderboard.sheet_by_stage')}</Text>
                {data.rows.map(row => {
                  const val = stageValue(row)
                  return (
                    <View key={row.stage.id_stage} style={{ gap: 6 }}>
                      <View style={styles.stageHead}>
                        <Text style={[styles.stageTitle, { color: tc.text }]}>{t('leaderboard.stage', { n: row.stage.id_stage_number })}</Text>
                        {val && (
                          <View style={styles.stageVal}>
                            <Text style={[styles.stageMain, { color: val.positive ? colors.success : tc.rosaText }]}>{val.main}</Text>
                            {!!val.sub && <Text style={[styles.stageSub, { color: tc.textMuted }]}>{val.sub}</Text>}
                          </View>
                        )}
                      </View>
                      {row.picks.length === 0 ? (
                        <Text style={[styles.none, { color: tc.textMuted }]}>{t('pick.no_pick_stage')}</Text>
                      ) : row.picks.map(p => {
                        const rd = data.riders[p.id]
                        return (
                          <RiderRow
                            key={p.slot}
                            slot={p.slot}
                            rd={rd}
                            national={false}
                            name={nameOf(rd, '—')}
                            right={(
                              <View style={styles.result}>
                                {p.res?.rank != null ? (
                                  <>
                                    <Text style={[styles.rank, { color: tc.text }]}>{p.res.rank}°</Text>
                                    {!!fmtGap(p.res.gap) && <Text style={[styles.gap, { color: tc.textMuted }]}>{fmtGap(p.res.gap)}</Text>}
                                  </>
                                ) : (
                                  <Text style={[styles.gap, { color: tc.textMuted }]}>{p.res?.dnf ? t('pick.result_dnf') : '—'}</Text>
                                )}
                              </View>
                            )}
                          />
                        )
                      })}
                      {row.flip && data.riders[row.flip.id_rider] && (
                        <View style={styles.flipRow}>
                          <MaterialCommunityIcons name="swap-horizontal" size={12} color={colors.flip} />
                          <Text style={styles.flipText}>{nameOf(data.riders[row.flip.id_rider], '')}</Text>
                          <Text style={[styles.flipSlot, { color: tc.textMuted }]}>{t('pick.flip_slot_arrow', { slot: row.flip.id_slot })}</Text>
                        </View>
                      )}
                    </View>
                  )
                })}
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '88%' },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderBottomWidth: 0.5 },
  title: { fontFamily: fonts.condensedBold, fontSize: 20 },
  sub: { fontSize: 12, marginTop: 1 },
  close: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  empty: { fontSize: 13, textAlign: 'center', paddingVertical: 24 },
  kicker: { fontSize: 12, fontFamily: fonts.condensedBold, textTransform: 'uppercase', letterSpacing: 0.8 },
  stageHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 2 },
  stageTitle: { fontFamily: fonts.condensedBold, fontSize: 15, textTransform: 'uppercase', letterSpacing: 0.5 },
  stageVal: { alignItems: 'flex-end' },
  stageMain: { fontFamily: fonts.condensedBold, fontSize: 16 },
  stageSub: { fontSize: 12 },
  none: { fontSize: 12, paddingHorizontal: 2 },
  result: { alignItems: 'flex-end', minWidth: 54 },
  rank: { fontFamily: fonts.condensedBold, fontSize: 18 },
  gap: { fontSize: 12 },
  flipRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 3 },
  flipText: { fontSize: 12, color: colors.flip, fontFamily: fonts.condensedBold, textTransform: 'uppercase' },
  flipSlot: { fontSize: 12 },
})
